import { Link, useNavigate } from "react-router-dom"
import { useState, useEffect, useRef } from "react"
import { MapPin, Search, Building2, Stethoscope, ScanLine, X, User } from "lucide-react"

export default function Herosectionwidget() {
  const navigate = useNavigate()
  const [selectedLocation, setSelectedLocation] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const [locations, setLocations] = useState([])
  const [results, setResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    fetch('/apis/web/clinics')
      .then(res => res.json())
      .then(data => {
        if (data.result) {
          setLocations(data.result.map(c => ({ id: c.Hospital_Id, name: c.HospitalName })))
        }
      })
      .catch(() => {})
    fetchResults('')
  }, [])

  const fetchResults = (search, clinicCode) => {
    setIsSearching(true)
    const params = new URLSearchParams({ search })
    if (clinicCode) params.append('clinicCode', clinicCode)
    fetch(`/apis/web/doctorsbysearch?${params}`)
      .then(res => res.json())
      .then(data => setResults(data.result || []))
      .catch(() => setResults([]))
      .finally(() => setIsSearching(false))
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchResults(searchQuery, selectedLocation)
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [searchQuery, selectedLocation])

  const doctors = results.filter(r => r.TYPE === 'DOCTOR')
  const specialities = results.filter(r => r.TYPE === 'SPECIALITY')

  const handleSelectItem = (item) => {
    setSearchQuery(item.NAME)
    setSelectedItem(item)
    setShowSearchDropdown(false)
  }

  const handleSearchChange = (val) => {
    setSearchQuery(val)
    setSelectedItem(null)
    setShowSearchDropdown(true)
  }

  const handleClearSearch = () => {
    setSearchQuery("")
    setSelectedItem(null)
    setShowSearchDropdown(false)
  }

  const buildBookNowUrl = () => {
    const params = new URLSearchParams()
    if (selectedLocation) params.append('location', selectedLocation)
    if (selectedItem) {
      params.append('search', selectedItem.NAME)
      if (selectedItem.TYPE === 'DOCTOR') params.append('doctorid', selectedItem.CODE)
      else if (selectedItem.TYPE === 'SPECIALITY') params.append('specializationId', selectedItem.CODE)
    } else if (searchQuery) {
      params.append('search', searchQuery)
    }
    return `/book-now${params.toString() ? '?' + params.toString() : ''}`
  }

  const handleBookAppointment = () => {
    navigate(buildBookNowUrl())
  }

  const renderDropdownItems = (size = 'desktop') => {
    const isDesktop = size === 'desktop'
    const px = isDesktop ? 'px-4' : 'px-3'
    const py = 'py-2'
    const iconSize = isDesktop ? 'w-7 h-7' : 'w-8 h-8'
    const iconInner = 'w-4 h-4'

    if (isSearching) return <p className={`${px} py-4 text-gray-500 text-center text-sm`}>Searching...</p>

    if (doctors.length > 0 || specialities.length > 0) {
      return (
        <>
          {doctors.length > 0 && (
            <div>
              <p className={`${px} py-2 text-xs font-semibold text-gray-500 bg-gray-50`}>Doctors</p>
              {doctors.map((doc, i) => (
                <button
                  key={`doc-${doc.CODE}-${i}`}
                  onClick={() => handleSelectItem(doc)}
                  className={`w-full ${px} ${py} flex items-center gap-2 hover:bg-gray-50 transition-colors text-left`}
                >
                  <div className={`${iconSize} rounded-full flex items-center justify-center shrink-0`} style={{backgroundColor: '#a52a2a1a'}}>
                    <User className={iconInner} style={{color: '#a52a2a'}} />
                  </div>
                  <span className="font-medium text-gray-800 text-sm">{doc.NAME}</span>
                </button>
              ))}
            </div>
          )}
          {specialities.length > 0 && (
            <div>
              <p className={`${px} py-2 text-xs font-semibold text-gray-500 bg-gray-50`}>Specialities</p>
              {specialities.map((spec, i) => (
                <button
                  key={`spec-${spec.CODE}-${i}`}
                  onClick={() => handleSelectItem(spec)}
                  className={`w-full ${px} ${py} flex items-center gap-2 hover:bg-gray-50 transition-colors text-left`}
                >
                  <div className={`${iconSize} rounded-full flex items-center justify-center shrink-0`} style={{backgroundColor: '#a52a2a1a'}}>
                    <Stethoscope className={iconInner} style={{color: '#a52a2a'}} />
                  </div>
                  <span className="font-medium text-gray-800 text-sm">{spec.NAME}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )
    }

    if (searchQuery) return <p className={`${px} py-4 text-gray-500 text-center text-sm`}>No results found</p>
    return null
  }

  return (
    <>
      {/* Desktop/Tablet */}
      <section className="hidden sm:block relative w-full overflow-hidden">
        <picture>
          <img src="/images/doctordesktop.jpeg" alt="Pranaam Hospitals Banner" className="w-full h-full object-contain" />
        </picture>
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4 md:px-8">
            <div className="max-w-2xl">
              {showSearchDropdown && <div className="fixed inset-0 z-20" onClick={() => setShowSearchDropdown(false)} />}
              <div className="relative w-full max-w-[700px] mb-6 z-30">
                <div className="bg-white rounded-2xl shadow-xl p-2 flex items-center gap-0">
                  <div className="flex items-center gap-1 px-3 py-3 border-r shrink-0 w-[200px]">
                    <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)} className="border-none outline-none bg-transparent text-sm font-medium cursor-pointer w-full truncate">
                      <option value="">All Locations</option>
                      {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 min-w-0 px-4 py-3 relative">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Search for Doctor or Speciality"
                        value={searchQuery}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        onFocus={() => setShowSearchDropdown(true)}
                        className="border-none outline-none w-full text-sm font-medium min-w-0"
                      />
                      {searchQuery && (
                        <button onClick={handleClearSearch} className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 shrink-0">
                          <X className="w-4 h-4 text-gray-600" />
                        </button>
                      )}
                    </div>
                    {showSearchDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border max-h-80 overflow-y-auto z-50">
                        {renderDropdownItems('desktop')}
                      </div>
                    )}
                  </div>
                  <button onClick={handleBookAppointment} className="px-8 py-3 text-white font-semibold rounded-lg text-sm whitespace-nowrap shrink-0" style={{backgroundColor: '#a52a2a'}}>
                    Book an Appointment
                  </button>
                </div>
              </div>
              {/* Cards */}
              <div className="grid grid-cols-3 gap-4 max-w-2xl">
                <Link to="/book-now" className="rounded-xl p-6 text-center hover:shadow-xl transition-shadow" style={{backgroundColor: '#a52a2a1a'}}>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Book Hospital</h3>
                  <p className="text-sm text-gray-600">Visit</p>
                </Link>
                <Link to="https://pranaam365.com/" className="rounded-xl p-6 text-center hover:shadow-xl transition-shadow" style={{backgroundColor: '#a52a2a1a'}}>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                    <Stethoscope className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Book Health</h3>
                  <p className="text-sm text-gray-600">Packages</p>
                </Link>
                <Link to="/book-now" className="rounded-xl p-6 text-center hover:shadow-xl transition-shadow" style={{backgroundColor: '#a52a2a1a'}}>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                    <ScanLine className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Book Video</h3>
                  <p className="text-sm text-gray-600">Consultations</p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile */}
      <section className="sm:hidden relative w-full">
        <img src="/images/mobiledesktop.jpeg" alt="Pranaam Hospitals Banner" className="w-full h-auto" />
        <div className="absolute bottom-0 left-0 right-0 bg-white px-4">
          <div className="space-y-2 relative py-4">
            <div className="flex items-center gap-2 px-3 py-2 border rounded-lg">
              <MapPin className="w-4 h-4 text-gray-400" />
              <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)} className="border-none outline-none bg-transparent text-sm flex-1">
                <option value="">All Locations</option>
                {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
              </select>
            </div>
            <div className="relative">
              <div className="flex items-center gap-2 px-3 py-2 border rounded-lg">
                <Search className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                  type="text"
                  placeholder="Search for Doctor or Speciality"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => setShowSearchDropdown(true)}
                  className="border-none outline-none w-full text-sm font-medium min-w-0"
                />
                {searchQuery && (
                  <button onClick={handleClearSearch} className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                )}
              </div>
            </div>
            {showSearchDropdown && (
              <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border max-h-60 overflow-y-auto z-50">
                {renderDropdownItems('mobile')}
              </div>
            )}
            {showSearchDropdown && <div className="fixed inset-0 z-20" onClick={() => setShowSearchDropdown(false)} />}
            <button onClick={handleBookAppointment} className="relative z-30 block w-full px-4 py-3 text-white font-medium rounded-lg text-center text-sm" style={{backgroundColor: '#a52a2a'}}>
              Book an Appointment
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-4">
            <Link to="/book-now" className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-red-50 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-xs font-semibold">Book Hospital</p>
              <p className="text-xs text-gray-600">Visit</p>
            </Link>
            <Link to="/book-now" className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-red-50 flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-xs font-semibold">Book Health</p>
              <p className="text-xs text-gray-600">Checkup</p>
            </Link>
            <Link to="/book-now" className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-red-50 flex items-center justify-center">
                <ScanLine className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-xs font-semibold">Book</p>
              <p className="text-xs text-gray-600">Scans</p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
